import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import SnackbarUtils from "utils/SnackbarUtils";
import { wmsCodeService } from "services";
import type { WmsCode } from "types";

type PersistMeta = {
  pinned?: boolean;
  onRevert?: () => void;
  onSuccess?: () => void;
  refetch?: boolean;
};

const WmsCodes: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<WmsCode | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [rows, setRows] = useState<WmsCode[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [confirmRow, setConfirmRow] = useState<WmsCode | null>(null);
  const [queryParams, setQueryParams] = useState<{ page: number; pageSize: number }>({
    page: 1,
    pageSize: 20,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await wmsCodeService.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      SnackbarUtils.error("Tải danh sách mã kho thất bại");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<ColDef<WmsCode>[]>(() => {
    return [
      {
        headerName: "Mã kho",
        field: "code",
        minWidth: 140,
      },
      {
        headerName: "Tên kho",
        field: "name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Mã tài khoản",
        field: "account_code",
        minWidth: 140,
      },
      {
        headerName: "Trọng lượng",
        field: "weight",
        minWidth: 120,
      },
      {
        headerName: "Ngưng hoạt động",
        field: "is_inactive",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["", "true", "false"] },
        valueFormatter: (params) =>
          params.value === true || params.value === "true"
            ? "Đang khóa"
            : params.value === false || params.value === "false"
              ? "Hoạt động"
              : "",
        valueParser: (params) => {
          const val = String(params.newValue);
          if (val === "true") return true;
          if (val === "false") return false;
          return undefined;
        },
      },
    ];
  }, []);

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      code: "",
      name: "",
      address: "",
      account_code: "",
      weight: "",
      is_inactive: false,
    };
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    async (row: Partial<WmsCode>, meta?: PersistMeta) => {
      const payload: WmsCode = {
        code: String(row.code ?? "").trim(),
        name: row.name ? String(row.name).trim() : undefined,
        address: row.address ? String(row.address).trim() : undefined,
        account_code: row.account_code ? String(row.account_code).trim() : undefined,
        weight: row.weight ? String(row.weight).trim() : undefined,
        is_inactive:
          /* row.is_inactive === "" || */ row.is_inactive === undefined
            ? undefined
            : Boolean(row.is_inactive),
      };

      if (!payload.code) {
        SnackbarUtils.error("Mã kho là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      setLoading(true);
      try {
        if (!row.id || meta?.pinned) {
          const res = await wmsCodeService.create(payload);
          if (res?.data) setRows((prev) => [res.data as WmsCode, ...prev]);
        } else {
          const res = await wmsCodeService.update(row.id, payload);
          if (res?.data) {
            setRows((prev) =>
              prev.map((item) => (item.id === row.id ? (res.data as WmsCode) : item)),
            );
          } else {
            setRows((prev) =>
              prev.map((item) => (item.id === row.id ? { ...item, ...payload } : item)),
            );
          }
        }
        if (meta?.refetch) fetchData();
        meta?.onSuccess?.();
      } catch (error) {
        SnackbarUtils.error("Lưu mã kho thất bại");
        meta?.onRevert?.();
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  const handleSaveRow = useCallback(
    (row: WmsCode, meta?: { pinned?: boolean }) => {
      persistRow(row, { pinned: meta?.pinned, refetch: meta?.pinned || !row.id });
    },
    [persistRow],
  );

  const handleDeleteRow = useCallback((row: WmsCode) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmRow?.id) return;
    setLoading(true);
    try {
      await wmsCodeService.destroy(confirmRow.id);
      setRows((prev) => prev.filter((item) => item.id !== confirmRow.id));
      setConfirmRow(null);
    } catch (error) {
      SnackbarUtils.error("Xóa mã kho thất bại");
    } finally {
      setLoading(false);
    }
  }, [confirmRow]);

  const handleQueryChange = useCallback((query: QueryState) => {
    setQueryParams((prev) => {
      const next = {
        ...prev,
        page: query.pagination?.page ?? prev.page,
        pageSize: query.pagination?.pageSize ?? prev.pageSize,
      };
      const unchanged = next.page === prev.page && next.pageSize === prev.pageSize;
      return unchanged ? prev : next;
    });
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (addRowRef.current ? { ...addRowRef.current } : undefined),
    [addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Quản lý mã kho (WMS)</h1>
        <p className="text-sm text-gray-600">
          Danh sách mã kho, cho phép thêm, sửa, xóa trực tiếp trên bảng.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Danh sách mã kho</h2>
            <p className="text-xs text-gray-500">Chỉnh sửa nhanh, lưu trực tiếp.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              resetInlineAddRow();
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Thêm nhanh
          </button>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={560}
          autoHeight={false}
          showAddRow="add"
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={Math.ceil(rows.length / queryParams.pageSize)}
          onSave={handleSaveRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          getRowId={(params) => String(params.data.id ?? `new-${params.rowIndex}`)}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
          onAfterActions={resetInlineAddRow}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang tải hoặc lưu dữ liệu mã kho."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa mã kho?"
        description={`Bạn chắc chắn muốn xóa mã kho "${confirmRow?.code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default WmsCodes;
